import { PAX } from "@pax/telemetry/attributes";
import { focusHaloFlush, panelMotion } from "@pax/ui";
import { ExternalLink } from "lucide-react";
import { type ReactNode, useState } from "react";
import { Timestamp, UserId } from "@/components/Humane";
import { formatDuration } from "@/features/evals/components";

const TOKEN =
  /("(?:\\.|[^"\\])*")(\s*:)?|\b(true|false|null)\b|(-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?)|([{}[\],])/g;
const ISO_TIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})$/;
const USER_KEYS = new Set(["userId", "responsibleUser", "ownerId", "authorId", "createdBy"]);
const PERSPECTIVE_KEYS = new Set(["perspectiveHandle"]);
const LIST_KINDS = new Map<string, IdKind>([
  ["userIds", "user"],
  ["perspectiveHandles", "perspective"],
]);
const AGENT_KEYS = new Set([PAX.agentId, "agentId"]);
/** Milliseconds in one unit of each duration key: `otel_traces` keeps nanoseconds. */
const DURATION_KEYS = new Map([
  ["Duration", 1e-6],
  ["durationMs", 1],
]);
const AGENT_ROLES = new Set(["gameMaster", "player", "grader"]);

/** The kind of id a string stands for, when it can be read as something friendlier. */
type IdKind = "user" | "perspective";

/**
 * A value as pretty-printed JSON, colored like the docs' code: keys in ink, data in amber.
 * Timestamps, durations, user ids, agent ids, and, given `names`, perspective ids can be pressed
 * to read them as a person would.
 */
export function JsonCode({
  value,
  names,
}: {
  value: unknown;
  names?: ReadonlyMap<string, string>;
}) {
  const lines = (JSON.stringify(value, null, 2) ?? "undefined").split("\n");
  const gutter = String(lines.length).length;
  const role = roleIn(value);
  let list: IdKind | null = null;
  let inPerspectiveRef = false;
  return (
    <pre className="overflow-x-auto rounded-xl bg-foreground/[0.04] py-3 font-mono text-xs leading-5">
      {lines.map((line, index) => {
        const trimmed = line.trimStart();
        if (list && trimmed.startsWith("]")) list = null;
        if (inPerspectiveRef && trimmed.startsWith("}")) inPerspectiveRef = false;
        if (/^"type":\s*"perspective",?$/.test(trimmed)) inPerspectiveRef = true;
        const idKey = /^"id":/.test(trimmed) && inPerspectiveRef ? "perspective" : null;
        const content = colored(line, list ?? idKey, role, names);
        const opens = /^\s*"(\w+)":\s*\[$/.exec(line);
        if (opens) list = LIST_KINDS.get(opens[1] ?? "") ?? null;
        return (
          <div
            // biome-ignore lint/suspicious/noArrayIndexKey: lines keep their order
            key={index}
            className="grid grid-cols-[auto_minmax(0,1fr)] gap-4 px-3 hover:bg-foreground/[0.03]"
          >
            <span
              className="text-right text-muted-foreground/50 tabular-nums select-none"
              style={{ width: `${gutter}ch` }}
            >
              {index + 1}
            </span>
            <span className="break-words whitespace-pre-wrap">{content}</span>
          </div>
        );
      })}
    </pre>
  );
}

function colored(
  line: string,
  idKind: IdKind | null,
  role: string | undefined,
  names: ReadonlyMap<string, string> | undefined,
) {
  const parts: ReactNode[] = [];
  let at = 0;
  let key: string | undefined;
  for (const match of line.matchAll(TOKEN)) {
    const index = match.index ?? 0;
    if (index > at) parts.push(line.slice(at, index));
    const [text, string, colon, keyword, number] = match;
    const className = string
      ? colon
        ? "text-foreground"
        : "text-amber-700 dark:text-amber-300"
      : keyword
        ? "text-blue-600 dark:text-blue-300"
        : number
          ? "text-amber-700 dark:text-amber-300"
          : "text-muted-foreground/70";
    const perMs = number ? DURATION_KEYS.get(key ?? "") : undefined;
    parts.push(
      <span key={index} className={className}>
        {perMs !== undefined ? (
          <Duration ms={Number(text) * perMs} stored={text} />
        ) : !string ? (
          text
        ) : colon ? (
          string
        ) : (
          humane(string, key, idKind, role, names)
        )}
      </span>,
    );
    if (string && colon) {
      key = JSON.parse(string) as string;
      parts.push(
        <span key={`${index}:`} className="text-muted-foreground/70">
          {colon}
        </span>,
      );
    }
    at = index + text.length;
  }
  if (at < line.length) parts.push(line.slice(at));
  return parts;
}

function humane(
  literal: string,
  key: string | undefined,
  idKind: IdKind | null,
  role: string | undefined,
  names: ReadonlyMap<string, string> | undefined,
): ReactNode {
  const text = JSON.parse(literal) as string;
  if (idKind === "user" || USER_KEYS.has(key ?? "")) return <>"{<UserId id={text} />}"</>;
  const name = names?.get(text);
  if (name && (idKind === "perspective" || PERSPECTIVE_KEYS.has(key ?? "")))
    return <>"{<PerspectiveId id={text} name={name} />}"</>;
  if (AGENT_KEYS.has(key ?? "")) return <>"{<AgentId id={text} role={role} />}"</>;
  if (ISO_TIME.test(text)) return <>"{<Timestamp at={text} />}"</>;
  return literal;
}

/** The first agent role anywhere in the value, so an agent id can open under the right tab. */
function roleIn(value: unknown): string | undefined {
  if (!value || typeof value !== "object") return undefined;
  for (const [key, inner] of Object.entries(value)) {
    if ((key === PAX.agentRole || key === "role") && typeof inner === "string") {
      if (AGENT_ROLES.has(inner)) return inner;
    }
    const found = roleIn(inner);
    if (found) return found;
  }
  return undefined;
}

const face = `rounded-md px-0.5 -mx-0.5 hover:bg-foreground/5 ${focusHaloFlush} ${panelMotion}`;

/** A duration read as a person would, which shows the number as stored when pressed. */
function Duration({ ms, stored }: { ms: number; stored: string }) {
  const [asStored, setAsStored] = useState(false);
  return (
    <button
      type="button"
      title={asStored ? formatDuration(ms) : `Stored as ${stored}`}
      onClick={() => setAsStored(!asStored)}
      className={`tabular-nums ${face}`}
    >
      {asStored ? stored : formatDuration(ms)}
    </button>
  );
}

/** A perspective id, which reads as the perspective's name when pressed. */
function PerspectiveId({ id, name }: { id: string; name: string }) {
  const [named, setNamed] = useState(false);
  return (
    <button
      type="button"
      title={named ? id : `Perspective ${name}`}
      onClick={() => setNamed(!named)}
      className={face}
    >
      {named ? name : id}
    </button>
  );
}

function AgentId({ id, role }: { id: string; role: string | undefined }) {
  const search = new URLSearchParams({ agentId: id, ...(role ? { role } : {}) });
  return (
    <span className="inline-flex items-baseline gap-0.5">
      {id}
      <a
        href={`/agents?${search}`}
        target="_blank"
        rel="noreferrer"
        aria-label="Open the agent in a new tab"
        title="Open the agent in a new tab"
        className={`self-center text-muted-foreground hover:text-foreground ${face}`}
      >
        <ExternalLink className="size-3" />
      </a>
    </span>
  );
}
